import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';

import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { AccordionModule } from 'primeng/accordion';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { ProductService, Product, PagedProductsRequest, PagedProductsResponse } from '../../../core/services/product.service';
import { AuthService } from '../../../core/services/auth';
import { Router } from '@angular/router';
import { InputIconModule } from 'primeng/inputicon';
import { HasPermissionDirective } from '../../../core/directives/has-permission.directive';
import { PermissionService } from '../../../core/services/permission.service';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-product-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    CardModule,
    BadgeModule,
    TooltipModule,
    DrawerModule,
    TagModule,
    IconFieldModule,
    AccordionModule,
    InputIconModule,
    HasPermissionDirective,
    LoadingSpinnerComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './product-management.html',
  styleUrl: './product-management.scss'
})
export class ProductManagement implements OnInit {
  @ViewChild('menu') menu!: any;
  
  products: Product[] = [];
  totalProducts = 0;
  loading = false;
  currentPage = 1;
  pageSize = 10;
  pageSizes = [10, 20, 50, 100];
  
  // Math property for template
  Math = Math;
  
  // Filters
  searchText = '';
  selectedStatus: any = null;
  selectedArchived: any = false;
  selectedCurrency: string | null = null;
  
  // Sorting - only by created date
  sortField = 'createdAt';
  sortOrder: 1 | -1 = -1; // -1 for descending (newest first)
  selectedSort = 'newest'; // Default sort option

  // Filter options
  statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];
  
  archivedOptions = [ 
    { label: 'Available', value: false },
    { label: 'Archived', value: true }
  ];

  getArchivedOptions() {
    return this.archivedOptions.map(option => ({
      ...option,
      disabled: option.value === this.selectedArchived
    }));
  }

  currencyOptions = [
    { label: 'All Currencies', value: null },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' },
    { label: 'CAD', value: 'CAD' },
    { label: 'AUD', value: 'AUD' },
    { label: 'JPY', value: 'JPY' },
    { label: 'CHF', value: 'CHF' },
    { label: 'CNY', value: 'CNY' },
    { label: 'INR', value: 'INR' }
  ];

  sortOptions = [
    { label: 'Newest First', value: 'newest' },
    { label: 'Oldest First', value: 'oldest' }
  ];
  
  // Product actions menu
  actionMenuItems: MenuItem[] = [];
  selectedProduct: Product | null = null;

  // Drawer for product details
  isDrawerVisible = false;
  selectedProductForReview: Product | null = null;

  // Additional loading states
  performingProductAction = false;

  constructor(
    private productService: ProductService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    // Set initial sort value based on current sortOrder
    this.selectedSort = this.sortOrder === -1 ? 'newest' : 'oldest';
    this.loadProducts();
    this.setupActionMenu();
  }

  setupActionMenu() {
    this.actionMenuItems = [];

    // Add View option (usually available to all)
    this.actionMenuItems.push({
      label: 'View',
      icon: 'pi pi-eye',
      command: () => this.viewProductDetails()
    });

    // Add Edit option if user has permission
    if (this.permissionService.hasPermission('product.update')) {
      this.actionMenuItems.push({
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editProduct()
      });
    }
  }

  updateActionMenuItems(product: Product) {
    this.actionMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editProduct()
      },
      {
        label: 'View',
        icon: 'pi pi-eye',
        command: () => this.viewProductDetails()
      }
    ];

    // Add conditional items based on product status
    if (product.isArchived) {
      // Only show restore if user has update permission
      if (this.permissionService.hasPermission('product.update')) {
        this.actionMenuItems.push({
          label: 'Restore Product',
          icon: 'pi pi-undo',
          command: () => this.restoreProduct()
        });
      }

    } else {
      if (product.isActive) {
        // Only show deactivate if user has update permission
        if (this.permissionService.hasPermission('product.update')) {
          this.actionMenuItems.push({
            label: 'Deactivate Product',
            icon: 'pi pi-ban',
            command: () => this.deactivateProduct()
          });
        }
      } else {
        // Only show activate if user has update permission
        if (this.permissionService.hasPermission('product.update')) {
          this.actionMenuItems.push({
            label: 'Activate Product',
            icon: 'pi pi-check',
            command: () => this.activateProduct()
          });
        }
      }
      
      // Only show archive if user has update permission
      if (this.permissionService.hasPermission('product.update')) {
        this.actionMenuItems.push({
          label: 'Archive Product',
          icon: 'pi pi-trash',
          command: () => this.archiveProduct()
        });
      }
    }
  }

  loadProducts() {
    this.loading = true;
    
    // Prepare filters - only include non-null/undefined values
    const filters: any = {};
    
    if (this.searchText && this.searchText.trim()) {
      filters.searchText = this.searchText.trim();
    }
    
    if (this.selectedStatus !== null && this.selectedStatus !== undefined) {
      filters.isActive = this.selectedStatus;
    }
    
    if (this.selectedArchived !== null && this.selectedArchived !== undefined) {
      filters.isArchived = this.selectedArchived;
    }

    if (this.selectedCurrency !== null && this.selectedCurrency !== undefined) {
      filters.currency = this.selectedCurrency;
    }

    const request: PagedProductsRequest = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      filters
    };

    this.productService.getPagedProducts(request).subscribe({
      next: (response: PagedProductsResponse) => {
        this.products = response.data || [];
        this.totalProducts = response.metadata?.[0]?.total || 0;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load products'
        });
        this.loading = false;
      }
    });
  }

  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadProducts();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadProducts();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadProducts();
  }

  onArchivedChange(event: any) {
    // Since the current option is disabled, the event.value will be the other option
    // Just use the event value directly since only the non-disabled option can be clicked
    this.selectedArchived = event.value;
    this.currentPage = 1;
    this.loadProducts();
  }

  resetFilters() {
    this.searchText = '';
    this.selectedStatus = null;
    this.selectedArchived = false;
    this.selectedCurrency = null;
    this.currentPage = 1;
    this.loadProducts();
  }

  onSortChange() {
    // Update sort field and order based on selection
    if (this.selectedSort === 'newest') {
      this.sortOrder = -1; // Descending (newest first)
    } else {
      this.sortOrder = 1; // Ascending (oldest first)
    }
    
    this.currentPage = 1;
    this.loadProducts();
  }

  toggleSort() {
    // Toggle between ascending and descending order
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.selectedSort = this.sortOrder === -1 ? 'newest' : 'oldest';
    this.currentPage = 1;
    this.loadProducts();
  }

  editProduct() {
    console.log('Edit product called, selectedProduct:', this.selectedProduct);
    if (this.selectedProduct) {
      this.router.navigate(['/admin/add-edit-product'], { 
        queryParams: { 
          id: this.selectedProduct._id,
          mode: 'edit'
        } 
      });
    }
  }

  viewProductDetails() {
    console.log('View product called, selectedProduct:', this.selectedProduct);
    if (this.selectedProduct) {
      console.log('Setting selectedProductForReview:', this.selectedProduct);
      this.selectedProductForReview = this.selectedProduct;
      this.isDrawerVisible = true;
      console.log('Drawer should now be visible, selectedProductForReview:', this.selectedProductForReview);
    } else {
      console.error('No selectedProduct available for viewing');
    }
  }

  addNewProduct() {
    this.router.navigate(['/admin/add-edit-product'], { 
      queryParams: { mode: 'add' } 
    });
  }

  activateProduct() {
    if (this.selectedProduct) {
      this.confirmationService.confirm({
        message: `Are you sure you want to activate "${this.selectedProduct.name}"?`,
        header: 'Confirm Activation',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        icon: 'pi pi-check-circle',
        accept: () => {
          this.performingProductAction = true;
          this.productService.updateProduct(this.selectedProduct!._id, { isActive: true }).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Product activated successfully'
              });
              this.loadProducts();
              this.performingProductAction = false;
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to activate product'
              });
              this.performingProductAction = false;
            }
          });
        }
      });
    }
  }

  deactivateProduct() {
    if (this.selectedProduct) {
      this.confirmationService.confirm({
        message: `Are you sure you want to deactivate "${this.selectedProduct.name}"?`,
        header: 'Confirm Deactivation',
        icon: 'pi pi-ban',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.productService.updateProduct(this.selectedProduct!._id, { isActive: false }).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Product deactivated successfully'
              });
              this.loadProducts();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to deactivate product'
              });
            }
          });
        }
      });
    }
  }

  archiveProduct() {
    if (this.selectedProduct) {
      this.confirmationService.confirm({
        message: `Are you sure you want to archive "${this.selectedProduct.name}"?`,
        header: 'Confirm Archive',
        icon: 'pi pi-archive',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.productService.archiveProduct(this.selectedProduct!._id).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Product archived successfully'
              });
              this.loadProducts();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to archive product'
              });
            }
          });
        }
      });
    }
  }

  restoreProduct() {
    if (this.selectedProduct) {
      this.confirmationService.confirm({
        message: `Are you sure you want to restore "${this.selectedProduct.name}"?`,
        header: 'Confirm Restore',
        icon: 'pi pi-undo',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.productService.restoreProduct(this.selectedProduct!._id).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Product restored successfully'
              });
              this.loadProducts();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to restore product'
              });
            }
          });
        }
      });
    }
  }



  onRowSelect(event: any) {
    console.log('Row selected:', event.data);
    this.selectedProduct = event.data;
    if (this.selectedProduct) {
      this.updateActionMenuItems(this.selectedProduct);
    }
  }

  onRowUnselect() {
    console.log('Row unselected');
    this.selectedProduct = null;
  }

  // Method to handle action button click and set selected product
  onActionClick(event: any, product: Product) {
    console.log('Action clicked for product:', product);
    this.selectedProduct = product;
    this.updateActionMenuItems(product);
    this.menu.toggle(event);
  }

  getStatusSeverity(product: Product): string {
    if (product.isArchived) return 'danger';
    if (!product.isActive) return 'warning';
    return 'success';
  }

  getStatusLabel(product: Product): string {
    if (product.isArchived) return 'Archived';
    if (!product.isActive) return 'Inactive';
    return 'Active';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  getAvailableTerritoriesCount(product: Product): string {
    if (!product.availableTerritories || product.availableTerritories.length === 0) {
      return '0/0';
    }
    
    // Use the new fields from the API response
    const selectedCount = product.availableTerritoryCount || product.availableTerritories.length;
    const totalCount = product.totalActiveTerritoryCount || 0;
    
    return `${selectedCount}/${totalCount}`;
  }

  getPriceDisplay(product: Product): string {
    const currency = product.currency || 'EUR';
    
    // If only price is available
    if (product.price && !product.minPrice && !product.maxPrice) {
      return `${product.price} ${currency}`;
    }
    
    // If only min and max prices are available
    if (product.minPrice && product.maxPrice && !product.price) {
      return `${product.minPrice} - ${product.maxPrice} ${currency}`;
    }
    
    // If both price and min/max are available
    if (product.price && (product.minPrice || product.maxPrice)) {
      let rangeText = '';
      if (product.minPrice && product.maxPrice) {
        rangeText = `(${product.minPrice} - ${product.maxPrice} ${currency})`;
      } else if (product.minPrice) {
        rangeText = `(${product.minPrice} ${currency})`;
      } else if (product.maxPrice) {
        rangeText = `(${product.maxPrice} ${currency})`;
      }
      return `${product.price} ${currency} - ${rangeText}`;
    }
    
    // If only min price is available
    if (product.minPrice && !product.maxPrice && !product.price) {
      return `${product.minPrice} ${currency}`;
    }
    
    // If only max price is available
    if (product.maxPrice && !product.minPrice && !product.price) {
      return `${product.maxPrice} ${currency}`;
    }
    
    return 'N/A';
  }

  getReservationsDisplay(product: Product): string {
    return product.reservations?.toString() || '0';
  }

  getTerritoriesByRegion(product: Product): string[] {
    if (!product.availableTerritories || product.availableTerritories.length === 0) {
      return [];
    }
    
    // Get unique regions from available territories
    const regions = [...new Set(product.availableTerritories.map(t => t.region || 'Unknown'))];
    return regions.sort();
  }

  getTerritoriesByRegionAndName(product: Product, region: string): any[] {
    if (!product.availableTerritories || product.availableTerritories.length === 0) {
      return [];
    }
    
    // Filter territories by region
    return product.availableTerritories.filter(t => (t.region || 'Unknown') === region);
  }

  // Method to safely render HTML content with proper link and list handling
  sanitizeHtml(content: string): SafeHtml {
    if (!content) return '';
    
    // Process the content to ensure links and lists work correctly
    let processedContent = content;
    
    // Ensure links have proper target="_blank" and rel="noopener noreferrer"
    processedContent = processedContent.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
    
    // Add CSS classes to links for styling
    processedContent = processedContent.replace(
      /<a\s+/gi,
      '<a class="content-link" '
    );
    
    // Add CSS classes to lists for proper styling
    processedContent = processedContent.replace(
      /<ul\s+/gi,
      '<ul class="content-list content-ul" '
    );
    
    processedContent = processedContent.replace(
      /<ol\s+/gi,
      '<ol class="content-list content-ol" '
    );
    
    // Handle lists without attributes
    processedContent = processedContent.replace(
      /<ul>/gi,
      '<ul class="content-list content-ul">'
    );
    
    processedContent = processedContent.replace(
      /<ol>/gi,
      '<ol class="content-list content-ol">'
    );
    
    // Add CSS classes to list items
    processedContent = processedContent.replace(
      /<li\s+/gi,
      '<li class="content-list-item" '
    );
    
    // Handle list items without attributes
    processedContent = processedContent.replace(
      /<li>/gi,
      '<li class="content-list-item">'
    );
    
    return this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }
}
